import * as React from 'react';

import { Box } from '@mui/material';

import { pick } from 'lodash';

import {
  FeedIcon,
  NoRequirementIcon,
  ReelIcon,
  StoryIcon,
  TikTokIcon,
  UserIcon,
} from '../../../components/Icons';
import InstagramTag, { InstagramTagProps } from '../../../components/InstagramTag/InstagramTag';
import { Offer, Reservation } from '../../../graphql/API';

const requirements = [
  'postOnInstagramFeed',
  'postOnInstagramReels',
  'postOnInstagramStories',
  'postOnInstagramStories3x',
  'includeSelfInPost',
  'postOnTikTokVideo',
] as const;

type Requirement = (typeof requirements)[number] | 'noRequirement';

export type RequirementItem = {
  Icon: typeof StoryIcon;
  label: React.ReactNode;
  InstagramTag?: React.ComponentType<InstagramTagProps>;
};

// eslint-disable-next-line react-refresh/only-export-components
const ReqLabel: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Box component='span' display='flex'>
    {children}
  </Box>
);

const dictRequirement: Record<Requirement, RequirementItem> = {
  includeSelfInPost: {
    Icon: UserIcon,
    label: (
      <ReqLabel>
        Include <b>&nbsp;yourself&nbsp;</b> in the post
      </ReqLabel>
    ),
    InstagramTag,
  },
  postOnInstagramFeed: {
    Icon: FeedIcon,
    label: (
      <ReqLabel>
        Post on <b>&nbsp;Feed&nbsp;</b>
      </ReqLabel>
    ),
    InstagramTag,
  },
  postOnInstagramStories: {
    Icon: StoryIcon,
    label: (
      <ReqLabel>
        Post on <b>&nbsp;Stories&nbsp;</b>
      </ReqLabel>
    ),
    InstagramTag,
  },
  postOnInstagramReels: {
    Icon: ReelIcon,
    label: (
      <ReqLabel>
        Post on <b>&nbsp;Reels&nbsp;</b>
      </ReqLabel>
    ),
    InstagramTag,
  },
  postOnInstagramStories3x: {
    Icon: StoryIcon,
    label: (
      <ReqLabel>
        Post on <b>&nbsp;Stories X3&nbsp;</b>
      </ReqLabel>
    ),
    InstagramTag,
  },
  postOnTikTokVideo: {
    Icon: TikTokIcon,
    label: (
      <ReqLabel>
        Post on <b>&nbsp;TikTok&nbsp;</b>
      </ReqLabel>
    ),
    InstagramTag,
  },
  noRequirement: {
    Icon: NoRequirementIcon,
    label: <ReqLabel>No Requirements</ReqLabel>,
  },
};

const getRequirements = (data: Offer) => {
  const values = pick(data, requirements);

  const reqs = Object.entries(values)
    .map(([k, v]) => (v === true ? k : undefined))
    .filter(Boolean) as Requirement[];

  const otherRequirements = requirements.filter((req) => req !== 'includeSelfInPost');

  const hasOtherRequirement = otherRequirements.some((req) => data[req as keyof Offer] === true);

  // If no other requirement is true, add "noRequirement"
  // This includes the case where only "includeSelfInPost" is true
  if (!hasOtherRequirement) {
    reqs.push('noRequirement');
  }

  return reqs.map((x) => dictRequirement[x]);
};

const getRequirementsOfReservation = (data: Reservation, isChatPage = false) => {
  const reqsToCheck = isChatPage ? requirements.slice(0, 4) : requirements;
  const values = pick(data, reqsToCheck);

  const reqs = Object.entries(values)
    .map(([k, v]) => (v === true ? k : undefined))
    .filter(Boolean) as Requirement[];

  const otherRequirements = reqsToCheck.filter((req) => req !== 'includeSelfInPost');

  const hasOtherRequirement = otherRequirements.some((req) => data[req as keyof Reservation] === true);

  // If no other requirement is true, add "noRequirement"
  // This includes the case where only "includeSelfInPost" is true
  if (!hasOtherRequirement) {
    reqs.push('noRequirement');
  }

  return reqs.map((x) => dictRequirement[x]);
};

export const useRequirementOfReservation = (data: Reservation | undefined, isChatPage = false) => {
  return React.useMemo(() => (data ? getRequirementsOfReservation(data, isChatPage) : []), [data]);
};

const useRequirement = (data: Offer | undefined) => {
  return React.useMemo(() => (data ? getRequirements(data) : []), [data]);
};

export default useRequirement;
